import { createModel } from '@rematch/core';
import { withMeasurementUnit } from 'utils/api';
import axios from 'axios';
import { toast, TOAST_TYPE } from 'components/RevxMessage';
import i18n from 'i18n/config';
import { RootModel } from 'models';
import excelDefinitionService from 'services/excelDefinitionService';
import { baseReducers, BaseState } from './baseReducers';

interface ExcelDefinitionState extends BaseState {}

export const excelDefinition = createModel<RootModel>()({
  state: {} as ExcelDefinitionState,
  reducers: {
    ...baseReducers<ExcelDefinitionState>(),
  },
  effects: (dispatch) => {
    const { excelDefinition } = dispatch;
    return {
      async importSku(formData: FormData): Promise<void> {
        try {
          await excelDefinitionService.importSku(formData);
          toast(i18n.t('toast.importFileSuccess'));
        } catch {
          toast(i18n.t('toast.importFileError'), TOAST_TYPE.error);
        }
      },
      async exportSku(payload: any, rootState): Promise<void> {
        let url = '';
        if (rootState.app.orgId === 1 || localStorage.getItem('org') === '1') {
          url = await excelDefinitionService.exportBaseSku();
        } else {
          url = await excelDefinitionService.exportOrgSku({
            isAll: true,
            orgIds:
              localStorage.getItem('org') || rootState.app.orgId.toString(),
          });
        }
        await excelDefinition.downloadFile(url);
      },
      async importEquipments(formData: FormData): Promise<void> {
        try {
          await excelDefinitionService.importEquipments(formData);
          toast(i18n.t('toast.importFileSuccess'));
        } catch {
          toast(i18n.t('toast.importFileError'), TOAST_TYPE.error);
        }
      },
      async exportEquipments(payload: any, rootState): Promise<void> {
        let url = '';
        if (rootState.app.orgId === 1 || localStorage.getItem('org') === '1') {
          url = await excelDefinitionService.exportBaseEquipments();
        } else {
          url = await excelDefinitionService.exportOrgEquipments({
            isAll: false,
            orgIds:
              localStorage.getItem('org') || rootState.app.orgId.toString(),
          });
        }
        await excelDefinition.downloadFile(url);
      },
      async importServices(formData: FormData): Promise<void> {
        try {
          await excelDefinitionService.importServices(formData);
          toast(i18n.t('toast.importFileSuccess'));
        } catch {
          toast(i18n.t('toast.importFileError'), TOAST_TYPE.error);
        }
      },
      async exportServices(payload: any, rootState): Promise<void> {
        let url = '';
        if (rootState.app.orgId === 1 || localStorage.getItem('org') === '1') {
          url = await excelDefinitionService.exportBaseServices();
        } else {
          url = await excelDefinitionService.exportOrgServices({
            isAll: false,
            orgIds:
              localStorage.getItem('org') || rootState.app.orgId.toString(),
          });
        }
        await excelDefinition.downloadFile(url);
      },
      async importMaterials(formData: FormData): Promise<void> {
        try {
          await excelDefinitionService.importMaterials(formData);
          toast(i18n.t('toast.importFileSuccess'));
        } catch {
          toast(i18n.t('toast.importFileError'), TOAST_TYPE.error);
        }
      },
      async exportMaterials(payload, rootState): Promise<void> {
        let url = '';
        if (rootState.app.orgId === 1 || localStorage.getItem('org') === '1') {
          url = await excelDefinitionService.exportBaseMaterials();
        } else {
          url = await excelDefinitionService.exportOrgMaterials({
            isAll: false,
            orgIds:
              localStorage.getItem('org') || rootState.app.orgId.toString(),
          });
        }
        await excelDefinition.downloadFile(url);
      },
      async importParcels(formData: FormData): Promise<void> {
        try {
          await excelDefinitionService.importParcels(formData);
          toast(i18n.t('toast.importFileSuccess'));
        } catch {
          toast(i18n.t('toast.importFileError'), TOAST_TYPE.error);
        }
      },
      async exportParcels(payload, rootState): Promise<void> {
        let url = '';
        if (rootState.app.orgId === 1 || localStorage.getItem('org') === '1') {
          url = await excelDefinitionService.exportBaseParcels();
        } else {
          url = await excelDefinitionService.exportOrgParcels({
            isAll: false,
            orgIds:
              localStorage.getItem('org') || rootState.app.orgId.toString(),
          });
        }
        await excelDefinition.downloadFile(url);
      },
      async downloadFile(url: string): Promise<void> {
        try {
          // Straight to axios rather than through utils/api, so the unit that wrapper puts on
          // every other URL has to be added by hand. Without it the export has no idea which
          // unit the user is working in and falls back to imperial - the sheet then says
          // IMPERIAL while the screen says metric.
          const res = await axios.get(withMeasurementUnit(url), {
            responseType: 'blob',
            headers: {
              'Access-Control-Allow-Credentials': true,
              'Access-Control-Allow-Origin': '*',
              Accept: '*/*',
              Authorization: localStorage.getItem('Authorization'),
            },
          });
          if (!(res.data instanceof Blob)) return;
          const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const fileName = decodeURIComponent(
            res.headers['content-disposition']
          )
            .split('=')[1]
            .replace(/utf-8''/, '');
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        } catch (e) {
          toast(i18n.t('errors.serviceUnavailable'), TOAST_TYPE.error);
        }
      },
    };
  },
});
